//* Libraries Imports
import { ChevronDownIcon, FilterIcon, XIcon } from "lucide-react";

//* UI Components
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";

//* Types & Schemas Imports
import { PaymentStatusSchema, type PaymentStatus } from "@/schemas/payment";

//* Helpers Imports
import { getStatusLabel } from "@helpers/paymentHelper";
import { handleAddToQuery, handleRemoveFromQuery } from "@helpers/query-helper";

//* Utils Imports
import { cn } from "@/lib/utils";

type SalesFiltersProps = {
  periodFilter: string;
  setPeriodFilter: (value: string) => void;
  statusFilter: PaymentStatus[];
  setStatusFilter: React.Dispatch<React.SetStateAction<PaymentStatus[]>>;
};

export function SalesFilters(props: SalesFiltersProps) {
  const handleToggleStatus = (status: PaymentStatus) => {
    if (props.statusFilter.includes(status)) {
      handleRemoveFromQuery(status, props.setStatusFilter);
    } else {
      handleAddToQuery(status, props.setStatusFilter);
    }
  };

  const selectedCount = props.statusFilter.length;

  return (
    <div className="flex flex-col md:flex-row items-end gap-4 pb-8">
      <div className="flex flex-col gap-2 w-full md:w-72">
        <Label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">
          PERÍODO
        </Label>
        <Select
          value={props.periodFilter}
          onValueChange={props.setPeriodFilter}
        >
          <SelectTrigger className="w-full bg-white border-gray-200 h-12 rounded-xl shadow-sm hover:border-blue-200 transition-all text-gray-700 font-medium">
            <SelectValue placeholder="SELECIONE O PERÍODO" />
          </SelectTrigger>
          <SelectContent className="rounded-xl border-gray-100 shadow-xl">
            <SelectItem value="Todos">TODOS OS PERÍODOS</SelectItem>
            <SelectItem value="Hoje">HOJE</SelectItem>
            <SelectItem value="Semana">ESTA SEMANA</SelectItem>
            <SelectItem value="Mes">ESTE MÊS</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col gap-2 w-full md:w-auto flex-1">
        <Label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">
          STATUS DO PAGAMENTO
        </Label>

        <div className="flex items-center gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "h-12 justify-between px-4 bg-white border-gray-200 rounded-xl shadow-sm hover:bg-gray-50 hover:border-blue-200 transition-all text-gray-700 font-medium min-w-[240px]",
                  selectedCount > 0 &&
                    "border-claroprimaria-01 bg-blue-50/30 text-claroprimaria-01 hover:bg-blue-50/50",
                )}
              >
                <div className="flex items-center gap-2">
                  <FilterIcon
                    className={cn(
                      "size-4",
                      selectedCount > 0
                        ? "text-claroprimaria-01"
                        : "text-gray-400",
                    )}
                  />
                  <span>
                    {selectedCount === 0
                      ? "Filtrar por status"
                      : selectedCount === 1
                        ? getStatusLabel(props.statusFilter[0])
                        : `${selectedCount} status selecionados`}
                  </span>
                </div>
                <ChevronDownIcon className="size-4 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="w-80 p-0 rounded-2xl shadow-2xl border-gray-100 overflow-hidden"
              align="start"
            >
              <div className="p-4 bg-gray-50/50 border-b border-gray-100 flex items-center justify-between">
                <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                  Selecionar Status
                </span>
                {selectedCount > 0 && (
                  <button
                    onClick={() => props.setStatusFilter([])}
                    className="text-[10px] font-bold text-red-500 hover:text-red-600 uppercase tracking-tight"
                  >
                    Limpar Tudo
                  </button>
                )}
              </div>
              <ScrollArea className="h-[320px]">
                <div className="p-2 space-y-1">
                  {PaymentStatusSchema.options.map((status) => {
                    const isSelected = props.statusFilter.includes(status);
                    return (
                      <div
                        key={status}
                        className={cn(
                          "flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer transition-all hover:bg-gray-50 group",
                          isSelected && "bg-blue-50/50 hover:bg-blue-50",
                        )}
                        onClick={() => handleToggleStatus(status)}
                      >
                        <Checkbox
                          id={`status-${status}`}
                          checked={isSelected}
                          onCheckedChange={() => handleToggleStatus(status)}
                          className={cn(
                            "size-5 rounded-md border-gray-300 transition-all",
                            isSelected &&
                              "border-claroprimaria-01 bg-claroprimaria-01",
                          )}
                        />
                        <span
                          className={cn(
                            "text-sm font-medium text-gray-600 transition-all group-hover:text-gray-900",
                            isSelected && "text-claroprimaria-01 font-semibold",
                          )}
                        >
                          {getStatusLabel(status)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
              <div className="p-3 bg-gray-50/50 border-t border-gray-100">
                <p className="text-[10px] text-gray-400 text-center font-medium">
                  {selectedCount} de {PaymentStatusSchema.options.length} status
                  selecionados
                </p>
              </div>
            </PopoverContent>
          </Popover>

          {selectedCount > 0 && (
            <div className="hidden lg:flex items-center gap-1.5 overflow-x-auto no-scrollbar max-w-[500px]">
              <Separator
                orientation="vertical"
                className="h-6 mx-1 bg-gray-200"
              />
              {props.statusFilter.map((status) => (
                <div
                  key={status}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-claroprimaria-01 rounded-full text-[10px] font-bold uppercase tracking-tight border border-blue-100 animate-in fade-in zoom-in duration-200"
                >
                  {getStatusLabel(status)}
                  <XIcon
                    className="size-3 cursor-pointer hover:text-red-500 transition-colors"
                    onClick={() => handleToggleStatus(status)}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
